import React, { Fragment, useEffect, useMemo } from "react";
import { CircularProgress, Container, Typography, useMediaQuery } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import HeroCarousel from "../components/HeroCarousel";
import ProductCard from "../components/ProductCard/ProductCard";
import { heroBannerData } from "../components/data/product";
import { useHomeProducts } from "../components/hooks/useHomeProducts";
import { useCategoryShowcase } from "../hooks/useCategoryShowcase";
import { Product } from "../types/Product";
import { HttpCache } from "../http/httpCache";
import { Icon, ImgPh, SectionHeader } from "../components/liquid/LiquidPrimitives";
import type { CategoryImage } from "../components/CategoryShowcase/ImageWithOverlay";

type SnackbarNavState = {
    snackbar?: {
        message: string;
        variant?: "default" | "error" | "success" | "warning" | "info";
    };
};

const normalizeProducts = (products: Product[], minCount: number): Product[] => {
    if (products.length === 0) return [];
    if (products.length >= minCount) return products;

    const normalized: Product[] = [...products];
    let index = 0;
    while (normalized.length < minCount) {
        normalized.push(products[index % products.length]);
        index++;
    }

    return normalized;
};

function CategoryTile({ image, size }: { image: CategoryImage; size: "main" | "top" | "small" }) {
    return (
        <a
            href={image.link}
            style={{
                position: "relative",
                display: "block",
                height: "100%",
                overflow: "hidden",
                borderRadius: 28,
                background: "linear-gradient(160deg, var(--cream-100), var(--cream-300))",
                boxShadow: "0 14px 40px -14px rgba(60,40,20,0.18), inset 0 1px 0 rgba(255,255,255,0.5)",
            }}
        >
            <ImgPh label={image.alt} src={image.src} ratio="auto" brightness={0.72} style={{ height: "100%", borderRadius: 0 }} />
            <div
                className="lg"
                style={{
                    position: "absolute",
                    left: 16,
                    right: 16,
                    bottom: 16,
                    padding: "16px 20px",
                    borderRadius: 18,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 14,
                }}
            >
                <div style={{ fontSize: size === "main" ? 20 : 16, fontWeight: 600, letterSpacing: "-0.02em", color: "var(--ink-900)" }}>
                    {image.title}
                </div>
                <div
                    style={{
                        width: 36,
                        height: 36,
                        borderRadius: 999,
                        background: "var(--ink-900)",
                        color: "var(--cream-50)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <Icon.arrowUpRight color="var(--cream-50)" />
                </div>
            </div>
        </a>
    );
}

function CategoryShowcaseSection({ images }: { images: CategoryImage[] }) {
    const isSmall = useMediaQuery("(max-width:900px)");

    if (images.length === 0) return null;

    return (
        <section style={{ padding: isSmall ? "32px 16px" : "48px 32px", position: "relative", zIndex: 2 }}>
            <SectionHeader
                eyebrow="Categorias"
                title="Explora la coleccion"
                action={<a href="/productos" className="pill">Ver todas <Icon.arrowUpRight /></a>}
            />
            <div style={{ display: "grid", gridTemplateColumns: isSmall || images.length === 1 ? "1fr" : "1fr 1fr", gap: 14, height: isSmall ? "auto" : 680 }}>
                <div style={{ height: isSmall ? 340 : "100%" }}>
                    <CategoryTile image={images[0]} size="main" />
                </div>
                {images.length > 1 && (
                    <div style={{ display: "grid", gridTemplateRows: isSmall ? "none" : "1fr 1fr", gap: 14, minHeight: 0 }}>
                        <div style={{ height: isSmall ? 240 : "100%" }}>
                            <CategoryTile image={images[1]} size="top" />
                        </div>
                        {images.length > 2 && (
                            <div style={{ display: "grid", gridTemplateColumns: images.length > 3 ? "1fr 1fr" : "1fr", gap: 14, minHeight: 0, height: isSmall ? 200 : "auto" }}>
                                <CategoryTile image={images[2]} size="small" />
                                {images.length > 3 && <CategoryTile image={images[3]} size="small" />}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}

function FeaturedSection({ products }: { products: Product[] }) {
    const isSmall = useMediaQuery("(max-width:900px)");

    return (
        <section style={{ padding: isSmall ? "40px 16px" : "60px 32px", position: "relative", zIndex: 2 }}>
            <SectionHeader
                eyebrow="Destacados de la semana"
                title="Productos destacados"
                action={
                    <div style={{ display: "flex", gap: 8 }}>
                        <button className="pill" style={{ padding: 12 }}><Icon.arrowLeft /></button>
                        <button className="pill" style={{ padding: 12 }}><Icon.arrowRight /></button>
                    </div>
                }
            />
            <div style={{ display: "grid", gridTemplateColumns: isSmall ? "repeat(2, 1fr)" : "repeat(3, 1fr)", gap: 18 }}>
                {products.slice(0, 6).map((product, index) => (
                    <ProductCard key={`${product.id}-${index}`} product={product} showDiscount={index === 0 || index === 2} discountText={index === 2 ? "Top" : "Nuevo"} />
                ))}
            </div>
        </section>
    );
}

function NovedadesSection({ products }: { products: Product[] }) {
    const isSmall = useMediaQuery("(max-width:900px)");

    return (
        <section style={{ padding: isSmall ? "40px 16px 60px" : "48px 32px 80px", position: "relative", zIndex: 2 }}>
            <div style={{ display: "grid", gridTemplateColumns: isSmall ? "1fr" : "0.95fr 2fr", gap: 18 }}>
                <div
                    style={{
                        position: "relative",
                        borderRadius: 32,
                        overflow: "hidden",
                        minHeight: isSmall ? 320 : 540,
                        background: "linear-gradient(165deg, #1B1815 0%, #0A0A0A 100%)",
                        color: "var(--cream-50)",
                        padding: isSmall ? "28px 24px" : "40px 36px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                    }}
                >
                    <div style={{ position: "relative" }}>
                        <div className="eyebrow" style={{ color: "var(--gold-soft)", marginBottom: 18 }}>Novedades</div>
                        <h3 style={{ fontSize: 56, lineHeight: 0.95, margin: 0, fontWeight: 500, letterSpacing: "-0.035em" }}>
                            Lo ultimo
                            <br />
                            en <em style={{ fontStyle: "normal", color: "var(--gold-soft)", fontFamily: "\"New York\", \"Times New Roman\", serif", fontWeight: 400 }}>granola y frutos secos</em>
                        </h3>
                        <p style={{ fontSize: 14, color: "rgba(255,250,240,0.6)", marginTop: 18, lineHeight: 1.55, maxWidth: 320 }}>
                            Descubri los productos que acabamos de sumar. Elaborados artesanalmente, frescos y sin conservantes.
                        </p>
                    </div>
                    <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 12 }}>
                        <div className="lg-dark" style={{ padding: "16px 20px", borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span style={{ fontSize: 13 }}>Nuevos ingresos</span>
                            <span className="mono" style={{ fontWeight: 600 }}>{products.length}</span>
                        </div>
                        <a className="btn-ghost" href="/productos" style={{ color: "var(--cream-50)", justifyContent: "center" }}>
                            Ver novedades <Icon.arrowUpRight />
                        </a>
                    </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 18 }}>
                    {products.slice(0, 4).map((product, index) => (
                        <ProductCard key={`${product.id}-${index}`} product={product} showDiscount={index === 1} discountText="Nuevo" />
                    ))}
                </div>
            </div>
        </section>
    );
}

function TrustRow() {
    const isSmall = useMediaQuery("(max-width:900px)");
    const items = [
        { icon: <Icon.truck />, title: "Envios a todo el pais", sub: "Despachamos en 24hs" },
        { icon: <Icon.shield />, title: "Calidad garantizada", sub: "Ingredientes naturales premium" },
        { icon: <Icon.spark />, title: "Produccion artesanal", sub: "Elaborado en cocina propia" },
    ];

    return (
        <section style={{ padding: isSmall ? "0 16px 48px" : "0 32px 60px", position: "relative", zIndex: 2 }}>
            <div className="lg-strong" style={{ padding: isSmall ? "20px 20px" : "28px 36px", borderRadius: 28, display: "grid", gridTemplateColumns: isSmall ? "repeat(2, 1fr)" : "repeat(3, 1fr)", gap: isSmall ? 16 : 24 }}>
                {items.map((item, index) => (
                    <div key={item.title} style={{ display: "flex", alignItems: "center", justifyContent: index === 0 ? "flex-start" : index === 1 ? "center" : "flex-end", gap: 14, paddingRight: !isSmall && index < 2 ? 24 : 0, borderRight: !isSmall && index < 2 ? "1px solid rgba(0,0,0,0.06)" : "none" }}>
                        <div style={{ width: 44, height: 44, borderRadius: 14, background: "rgba(10,10,10,0.06)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink-900)" }}>
                            {item.icon}
                        </div>
                        <div>
                            <div style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: "-0.01em" }}>{item.title}</div>
                            <div style={{ fontSize: 12, color: "var(--ink-500)", marginTop: 2 }}>{item.sub}</div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

const Home: React.FC = () => {
    const { showcaseProducts, novedadesProducts, loading, error } = useHomeProducts();
    const { images: categoryImages } = useCategoryShowcase(4);
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        HttpCache.getPersonTypes();
        HttpCache.getIdentityDocumentTypes();
    }, []);

    useEffect(() => {
        const state = location.state as SnackbarNavState | null;
        const snack = state?.snackbar;

        if (snack?.message) {
            enqueueSnackbar(snack.message, { variant: snack.variant ?? "default" });
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [enqueueSnackbar, location.pathname, location.state, navigate]);

    const featured = useMemo(() => normalizeProducts(showcaseProducts, 6), [showcaseProducts]);
    const latest = useMemo(() => normalizeProducts(novedadesProducts, 4), [novedadesProducts]);
    const slidesHero = useMemo(
        () => [
            heroBannerData,
            ...categoryImages.map((image) => ({
                type: "category" as const,
                ...image,
            })),
        ],
        [categoryImages]
    );

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2 }}>Cargando productos...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
                <Typography variant="h4" color="error" gutterBottom>{error}</Typography>
                <Typography variant="body1" color="text.secondary">No pudimos cargar los productos en este momento.</Typography>
            </Container>
        );
    }

    return (
        <Fragment>
            <HeroCarousel slides={slidesHero} />
            <CategoryShowcaseSection images={categoryImages} />
            {featured.length > 0 && <FeaturedSection products={featured} />}
            {latest.length > 0 && <NovedadesSection products={latest} />}
            <TrustRow />
        </Fragment>
    );
};

export default Home;
